/**
 * PricingCard — single pricing tier.  `featured` highlights the recommended
 * tier with a primary border + lift.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { COLOR, FONT, RADIUS, ELEVATION } from '../../../components/ss/tokens';

function isExternal(href) {
  return /^(https?:|mailto:|tel:)/i.test(href || '');
}

function CTA({ link, featured }) {
  if (!link?.href) return null;
  const styles = featured
    ? {
        background: COLOR.blue,
        color: '#fff',
        padding: '12px 22px',
        borderRadius: RADIUS.pill,
        fontSize: FONT.size.md,
        fontWeight: FONT.weight.semi,
        textDecoration: 'none',
        display: 'inline-block',
        boxShadow: ELEVATION.sm,
      }
    : {
        background: 'transparent',
        color: COLOR.blue,
        padding: '12px 22px',
        borderRadius: RADIUS.pill,
        fontSize: FONT.size.md,
        fontWeight: FONT.weight.semi,
        textDecoration: 'none',
        display: 'inline-block',
        border: `1px solid ${COLOR.blueBorder}`,
      };
  if (isExternal(link.href) || link.href.includes('#')) {
    return <a href={link.href} style={styles}>{link.label}</a>;
  }
  return <Link to={link.href} style={styles}>{link.label}</Link>;
}

export default function PricingCard({ tier }) {
  const { name, price, period, description, highlights = [], cta, featured } = tier;
  return (
    <div
      style={{
        background: '#fff',
        border: featured ? `2px solid ${COLOR.blue}` : `1px solid ${COLOR.grayBorder}`,
        borderRadius: RADIUS.lg,
        padding: 32,
        boxShadow: featured ? ELEVATION.lg : ELEVATION.xs,
        position: 'relative',
        transform: featured ? 'translateY(-6px)' : 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
      }}
    >
      {featured && (
        <span
          style={{
            position: 'absolute',
            top: -14,
            left: '50%',
            transform: 'translateX(-50%)',
            background: COLOR.blue,
            color: '#fff',
            fontSize: FONT.size.sm,
            fontWeight: FONT.weight.bold,
            padding: '4px 12px',
            borderRadius: RADIUS.pill,
            textTransform: 'uppercase',
            letterSpacing: '1px',
          }}
        >
          Most popular
        </span>
      )}

      <div>
        <h3
          style={{
            margin: 0,
            fontSize: 18,
            fontWeight: FONT.weight.bold,
            color: COLOR.text,
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
          }}
        >
          {name}
        </h3>
        <div style={{ marginTop: 12 }}>
          <span
            style={{
              fontSize: 38,
              fontWeight: FONT.weight.xbold,
              color: COLOR.text,
              letterSpacing: '-0.6px',
            }}
          >
            {price}
          </span>
          {period && (
            <span style={{ fontSize: FONT.size.md, color: COLOR.textMuted, marginLeft: 4 }}>
              {period}
            </span>
          )}
        </div>
        {description && (
          <p style={{ marginTop: 10, marginBottom: 0, fontSize: FONT.size.md, color: COLOR.textMuted, lineHeight: 1.55 }}>
            {description}
          </p>
        )}
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {highlights.map((h, i) => (
          <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: FONT.size.md, color: COLOR.text, lineHeight: 1.5 }}>
            <span style={{ color: COLOR.green, fontWeight: FONT.weight.bold, flexShrink: 0 }}>✓</span>
            <span>{h}</span>
          </li>
        ))}
      </ul>

      <div style={{ marginTop: 'auto', paddingTop: 6 }}>
        <CTA link={cta} featured={featured} />
      </div>
    </div>
  );
}
